import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { IGTFSStation } from "../../../interfaces/endpoints/IGTFS.types";
import { getGTFSStationIfAny, resetGTFSStationToStation } from "../../../services/GTFS.service";

// Which of the two followed fields a reset applies to; both at once is one request.
export interface GtfsFollowFields {
  name?: boolean;
  location?: boolean;
}

const describe = (fields: GtfsFollowFields, name: string, location: string, both: string) =>
  fields.name && fields.location ? both : fields.name ? name : location;

// Whether the GTFS feed follows this station for its name and its location. A GTFS stop follows
// the station unless that field was edited on the GTFS stations screen; while it is edited, a
// change made on the station page never reaches Google. `follow` makes the feed follow the
// station again for the given fields (the backend copies the station's current values and clears
// the flags), in one request even when both are asked for.
export const useGtfsFollow = (stationId: number | undefined, enabled: boolean) => {
  const [stop, setStop] = useState<IGTFSStation | null>(null);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (!enabled || !stationId) return;
    getGTFSStationIfAny(stationId)
      .then((response) => setStop(response.status === 200 ? response.data : null)) // 404: no GTFS stop
      .catch(() => setStop(null)); // no permission or a network error: nothing to warn about
  }, [stationId, enabled]);

  const follow = useCallback(
    async (fields: GtfsFollowFields): Promise<boolean> => {
      if (!stationId || (!fields.name && !fields.location)) return false;
      setResetting(true);
      try {
        const response = await resetGTFSStationToStation(stationId, {
          name: !!fields.name,
          location: !!fields.location
        });
        if (response.status === 400) {
          toast.error(
            describe(
              fields,
              "Stacioni nuk ka emër në anglisht, GTFS mbetet me emrin e vet",
              "Stacioni nuk ka koordinata, GTFS mbetet me vendndodhjen e vet",
              "Stacioni nuk ka emër në anglisht ose koordinata, GTFS mbetet me vlerat e veta"
            ),
            { duration: 3500 }
          );
          return false;
        }
        if (response.status !== 200) {
          toast.error("Veprimi dështoi", { duration: 3500 });
          return false;
        }
        setStop(response.data);
        toast.success(
          describe(
            fields,
            "GTFS tani ndjek emrin e stacionit",
            "GTFS tani ndjek vendndodhjen e stacionit",
            "GTFS tani ndjek emrin dhe vendndodhjen e stacionit"
          )
        );
        return true;
      } catch (error) {
        toast.error("Veprimi dështoi", { duration: 3500 });
        console.error("Error making the GTFS stop follow the station:", error);
        return false;
      } finally {
        setResetting(false);
      }
    },
    [stationId]
  );

  return {
    follow,
    resetting,
    nameEdited: !!stop?.isNameEdited,
    locationEdited: !!stop?.isLocationEdited
  };
};
